/**
 * Botão "Relatar" do topo: a gerente conta o que achou estranho, o que quer
 * mudar ou o que não entendeu, sem sair da tela. Vai junto, sozinho: a tela
 * aberta (endereço), a versão do sistema, o tamanho da tela e o último erro da
 * API (com o código do pedido, que leva ao log do servidor). Os relatos ficam
 * na tela Relatos. O texto em andamento fica guardado nesta aba (se a sessão
 * acabar no meio, ele volta quando a pessoa entrar de novo).
 *
 * Lê: a rota atual e o último erro (dados/api.ts).
 * Usado por: App.tsx.
 */
import { useRef, useState, type FormEvent } from 'react';
import { api, ErroApi, ultimoErroDaApi, type TipoDeRelato } from '../dados/api.ts';
import { linkPara } from '../rotas.ts';
import { VERSAO_DO_SITE } from '../versao.ts';
import { ErroNaTela } from './basicos.tsx';

const TIPOS: Array<{ id: TipoDeRelato; rotulo: string; pergunta: string }> = [
  { id: 'problema', rotulo: 'Problema', pergunta: 'O que está errado? (ex.: "o total da Amend do Emival deu R$ 15.139, na minha planilha é R$ 15.400")' },
  { id: 'sugestao', rotulo: 'Sugestão', pergunta: 'O que você gostaria que o sistema fizesse?' },
  { id: 'duvida', rotulo: 'Dúvida', pergunta: 'O que não ficou claro?' },
];

const RASCUNHO = 'alianca-relato-em-andamento';

function lerRascunho(): string {
  try {
    return sessionStorage.getItem(RASCUNHO) ?? '';
  } catch {
    return ''; // sem sessionStorage (aba anônima): sem rascunho
  }
}

function guardarRascunho(texto: string): void {
  try {
    if (texto === '') sessionStorage.removeItem(RASCUNHO);
    else sessionStorage.setItem(RASCUNHO, texto);
  } catch {
    // sem sessionStorage: tudo bem
  }
}

export function BotaoRelatar() {
  const janela = useRef<HTMLDialogElement>(null);
  const [tipo, setTipo] = useState<TipoDeRelato>('problema');
  const [texto, setTextoNaTela] = useState(lerRascunho);
  const setTexto = (novo: string) => {
    setTextoNaTela(novo);
    guardarRascunho(novo);
  };
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState<ErroApi | null>(null);

  function abrir() {
    setEnviado(false);
    setErro(null);
    janela.current?.showModal();
  }

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    setEnviando(true);
    setErro(null);
    try {
      await api.criarRelato({
        tipo,
        texto,
        tela: window.location.hash || '#/',
        contexto: {
          versao: VERSAO_DO_SITE,
          tela: `${window.innerWidth} × ${window.innerHeight}`,
          navegador: navigator.userAgent,
          ultimoErro: ultimoErroDaApi(),
        },
      });
      setTexto('');
      setEnviado(true);
    } catch (falha) {
      setErro(falha instanceof ErroApi ? falha : new ErroApi('APP', String(falha), 0, null));
    } finally {
      setEnviando(false);
    }
  }

  const atual = TIPOS.find((t) => t.id === tipo) ?? TIPOS[0]!;

  return (
    <>
      <button type="button" className="botao-secundario botao-relatar" onClick={abrir}>
        Relatar
      </button>
      <dialog ref={janela} className="janela" aria-labelledby="titulo-relatar">
        {enviado ? (
          <div className="pilha">
            <h2 id="titulo-relatar">Recebido, obrigado!</h2>
            <p>
              O relato foi guardado com a tela que estava aberta. Acompanhe a resposta em{' '}
              <a href={linkPara({ tela: 'relatos' })} onClick={() => janela.current?.close()}>
                Relatos
              </a>
              .
            </p>
            <div className="linha-campos">
              <button type="button" className="botao" onClick={() => janela.current?.close()}>
                Fechar
              </button>
              <button type="button" className="botao-secundario" onClick={() => setEnviado(false)}>
                Relatar outra coisa
              </button>
            </div>
          </div>
        ) : (
          <form className="pilha" onSubmit={enviar} method="dialog">
            <h2 id="titulo-relatar">Relatar</h2>
            <div className="segmentado" role="group" aria-label="Tipo do relato">
              {TIPOS.map((opcao) => (
                <button key={opcao.id} type="button" aria-pressed={opcao.id === tipo} onClick={() => setTipo(opcao.id)}>
                  {opcao.rotulo}
                </button>
              ))}
            </div>
            <label>
              {atual.pergunta}
              <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={5} maxLength={4000} required />
            </label>
            <p className="texto-apagado texto-pequeno">Vai junto, sozinho: esta tela ({window.location.hash || 'início'}), a versão do sistema e o último erro que apareceu.</p>
            {erro ? <ErroNaTela erro={erro} /> : null}
            <div className="linha-campos">
              <button type="submit" className="botao" disabled={enviando || texto.trim() === ''}>
                {enviando ? 'Enviando…' : 'Enviar'}
              </button>
              <button type="button" className="botao-secundario" onClick={() => janela.current?.close()}>
                Cancelar
              </button>
            </div>
          </form>
        )}
      </dialog>
    </>
  );
}
